import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOverlay } from '../context/OverlayContext';
import OpponentDetailsOverlay from '../components/OpponentDetailsOverlay';
import { handleAuthError } from '../utils/auth';
import { detectCurrentLocation } from '../utils/locationService';

const NewMatchPage = () => {
  const navigate = useNavigate();
  const { showOverlay } = useOverlay();
  
  const [matchSurface, setMatchSurface] = useState('Hard court');
  const [matchLocation, setMatchLocation] = useState('');
  const [detectingLocation, setDetectingLocation] = useState(false);
  
  // Player selection states
  const [opponents, setOpponents] = useState([]);
  const [player1Id, setPlayer1Id] = useState('me');
  const [player1Name, setPlayer1Name] = useState('Me');
  const [player2Id, setPlayer2Id] = useState('');
  const [player2Name, setPlayer2Name] = useState('');
  const [searchTermP2, setSearchTermP2] = useState('');
  const [showP2Dropdown, setShowP2Dropdown] = useState(false);
  const [showOpponentOverlay, setShowOpponentOverlay] = useState(false);

  // Match Format Configuration State
  const [numberOfSets, setNumberOfSets] = useState(3);
  const [gamesPerSet, setGamesPerSet] = useState(6);
  const [adScoring, setAdScoring] = useState('ad'); // 'ad' (with Advantage) or 'no_ad' (sudden death at 40-40)
  const [tieBreakAt, setTieBreakAt] = useState('at_games'); // 'at_games' (6:6 -> 7:6) or 'at_minus_one' (5:5 -> 6:5)
  const [tieBreakTarget, setTieBreakTarget] = useState(7); // 5, 7, 10
  const [finalSetType, setFinalSetType] = useState('super_tiebreak'); // 'super_tiebreak' or 'regular'
  const [superTieBreakTarget, setSuperTieBreakTarget] = useState(10); // 7, 10
  const [firstServer, setFirstServer] = useState('ask'); // 'ask', 'me', 'opponent'

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      handleAuthError(navigate);
      return null;
    }
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  };

  const handleSelectPlayer1 = (id, name) => {
    setPlayer1Id(id);
    setPlayer1Name(name);
  };

  const handleSelectPlayer2 = (id, name) => {
    setPlayer2Id(id);
    setPlayer2Name(name);
    setSearchTermP2(name);
    setShowP2Dropdown(false);
  };

  // Fetch opponents on component mount
  useEffect(() => {
    const fetchOpponents = async () => {
      try {
        const headers = getAuthHeaders();
        if (!headers) return;

        const response = await fetch('/best3intennis-api/api/get_opponents.php', {
          method: 'GET',
          headers: headers,
          credentials: 'include',
        });

        if (!response.ok) {
          const result = await response.json().catch(() => null);
          if (result && (result.message?.includes('token') || result.error?.includes('token'))) {
            handleAuthError(navigate);
            return;
          }
          throw new Error(result?.message || 'Failed to fetch opponents.');
        }

        const result = await response.json();
        
        // Process opponents to ensure firstName and lastName are available
        const processedOpponents = (result || []).map(opponent => {
          if (opponent.name && !opponent.firstName && !opponent.lastName) {
            const nameParts = opponent.name.split(' ');
            return {
              ...opponent,
              firstName: nameParts[0] || '',
              lastName: nameParts.slice(1).join(' ') || '',
            };
          }
          return opponent;
        });
        setOpponents(processedOpponents);
      } catch (error) {
        console.error('Error fetching opponents:', error);
        if (error.message.includes('token')) {
          handleAuthError(navigate);
        } else {
          showOverlay(`Failed to load opponents: ${error.message}`, 'error');
        }
      }
    };
    fetchOpponents();
  }, [navigate, showOverlay]);

  const handleAddOpponent = () => {
    setShowOpponentOverlay(true);
  };

  const handleOpponentSave = async (newOpponentData) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const url = newOpponentData.id 
        ? '/best3intennis-api/api/update_opponent.php' 
        : '/best3intennis-api/api/add_opponent.php';
      const method = newOpponentData.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: headers,
        body: JSON.stringify(newOpponentData),
        credentials: 'include',
      });

      if (!response.ok) {
        const result = await response.json().catch(() => null);
        if (result && (result.message?.includes('token') || result.error?.includes('token'))) {
          handleAuthError(navigate);
          return;
        }
        throw new Error(result?.message || 'Failed to save opponent.');
      }

      const result = await response.json();
      showOverlay(result.message || 'Opponent saved successfully!', 'success');
      
      // Refresh opponents list
      const fetchResponse = await fetch('/best3intennis-api/api/get_opponents.php', {
        headers: headers,
        credentials: 'include',
      });
      const data = await fetchResponse.json();
      const processed = (data || []).map(opponent => {
        if (opponent.name && !opponent.firstName && !opponent.lastName) {
          const parts = opponent.name.split(' ');
          return { ...opponent, firstName: parts[0] || '', lastName: parts.slice(1).join(' ') || '' };
        }
        return opponent;
      });
      setOpponents(processed);

      // Auto select newly created opponent for Player 2 if not selected yet
      if (!player2Id && result.opponentId) {
        const createdName = `${newOpponentData.firstName} ${newOpponentData.lastName}`.trim();
        handleSelectPlayer2(result.opponentId, createdName);
      }
      
      setShowOpponentOverlay(false);
    } catch (error) {
      console.error('Error saving opponent:', error);
      showOverlay(`Error: ${error.message}`, 'error');
    }
  };

  const handleOpponentCancel = () => {
    setShowOpponentOverlay(false);
  };

  const handleAutoDetectLocation = async () => {
    setDetectingLocation(true);
    try {
      const res = await detectCurrentLocation();
      if (res && res.success && res.location) {
        setMatchLocation(res.location);
      } else if (res && res.error) {
        showOverlay(res.error, 'info');
      }
    } catch (_e) {
      showOverlay('Unable to detect current location.', 'error');
    } finally {
      setDetectingLocation(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!player2Id) {
      showOverlay('Please select Player 2 for this match.', 'error');
      return;
    }

    if (player1Id !== 'me' && String(player1Id) === String(player2Id)) {
      showOverlay('Player 1 and Player 2 cannot be the same player.', 'error');
      return;
    }

    const finalMatchName = `${player1Name} vs ${player2Name}`;

    const matchData = {
      matchName: finalMatchName,
      matchDescription: '',
      matchLocation,
      location: matchLocation,
      matchSurface,
      surface: matchSurface,
      opponentId: player2Id,
      player1Id,
      player1Name,
      player2Id,
      player2Name,
      matchConfig: {
        numberOfSets,
        gamesPerSet,
        adScoring,
        tieBreakAt,
        tieBreakTarget,
        finalSetType,
        superTieBreakTarget,
        surface: matchSurface,
        player1Id,
        player1Name,
        player2Id,
        player2Name,
        firstServer,
      },
    };

    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch('/best3intennis-api/api/create_match.php', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(matchData),
        credentials: 'include',
      });

      if (!response.ok) {
        const result = await response.json().catch(() => null);
        if (result && (result.message?.includes('token') || result.error?.includes('token'))) {
          handleAuthError(navigate);
          return;
        }
        throw new Error(result?.message || 'Failed to create match.');
      }
      
      const result = await response.json();
      if (result.matchId) {
        // Save initial match state to local storage cache so MatchTrackerPage loads seamlessly
        const initialMatchState = {
          id: result.matchId,
          match_id: result.matchId,
          matchData: {
            id: result.matchId,
            match_id: result.matchId,
            name: finalMatchName,
            match_name: finalMatchName,
            player1_id: player1Id,
            player1_name: player1Name,
            player2_id: player2Id,
            player2_name: player2Name,
            opponent_name: player2Name,
            opponent_id: player2Id,
            surface: matchSurface,
            location: matchLocation,
            match_config: matchData.matchConfig,
            matchConfig: matchData.matchConfig,
            status: 'inprogress',
            created_at: new Date().toISOString(),
          },
          currentState: {
            setNumber: 1,
            gameNumber: 1,
            myGamesWonInSet: 0,
            opponentGamesWonInSet: 0,
            mySetsWon: 0,
            opponentSetsWon: 0,
            serving: (firstServer === 'opponent') ? 'opponent' : (firstServer === 'me' ? 'me' : null),
            initialSetServer: (firstServer === 'opponent') ? 'opponent' : (firstServer === 'me' ? 'me' : null),
            servingSelected: Boolean(firstServer && firstServer !== 'ask'),
            matchScores: [],
            matchCompleted: false,
            matchConfig: matchData.matchConfig,
            player1Id,
            player1Name,
            player2Id,
            player2Name,
          },
          pointState: { me: 0, opp: 0 },
          myMistakes: { ace: 0, winner: 0, serveFault: 0, firstServeFault: 0, doubleFault: 0, unforcedError: 0, forcedError: 0 },
          opponentMistakes: { ace: 0, winner: 0, serveFault: 0, firstServeFault: 0, doubleFault: 0, unforcedError: 0, forcedError: 0 },
          serveAttempt: 1,
          completedGames: [],
          updated_at: Date.now(),
        };

        try {
          localStorage.setItem(`local_active_match_${result.matchId}`, JSON.stringify(initialMatchState));
        } catch (_e) {}

        navigate(`/match-tracker/${result.matchId}`);
      } else {
        throw new Error('Match created but no match ID returned.');
      }
    } catch (error) {
      console.error('Error creating match:', error);
      if (error.message.includes('token')) {
        handleAuthError(navigate);
      } else {
        showOverlay(`Failed to create match: ${error.message}`, 'error');
      }
    }
  };

  // Filter opponents for Player 2 (excluding Player 1 if selected)
  const filteredOpponentsP2 = opponents.filter(opponent => {
    if (player1Id !== 'me' && String(opponent.id) === String(player1Id)) return false;
    const fullName = `${opponent.firstName || ''} ${opponent.lastName || ''} ${opponent.name || ''}`.toLowerCase();
    return fullName.includes(searchTermP2.toLowerCase());
  });

  return (
    <div className="container mx-auto p-4 max-w-lg">
      <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">🎾 New Match</h2>

      <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-lg p-6 space-y-5">
        {/* 1. Player 1 Selection (Single Line Row) */}
        <div>
          <label htmlFor="player1Select" className="block text-gray-700 text-sm font-bold mb-1.5">
            Player 1 (Green Side 🟢)
          </label>
          <select
            id="player1Select"
            value={player1Id}
            onChange={(e) => {
              const val = e.target.value;
              if (val === 'me') {
                handleSelectPlayer1('me', 'Me');
              } else {
                const opp = opponents.find(o => String(o.id) === String(val));
                if (opp) {
                  const name = `${opp.firstName || ''} ${opp.lastName || ''}`.trim() || opp.name;
                  handleSelectPlayer1(opp.id, name);
                }
              }
            }}
            className="shadow appearance-none border border-gray-300 rounded w-full py-2.5 px-3 text-gray-700 leading-tight focus:outline-none focus:border-green-500 text-sm bg-white"
          >
            <option value="me">👤 Me (You)</option>
            {opponents.map(op => (
              <option key={op.id} value={op.id}>
                {op.firstName} {op.lastName} {op.utr ? `(UTR: ${op.utr})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* 2. Player 2 Searchable Dropdown (Single Line Row) */}
        <div className="relative">
          <div className="flex justify-between items-center mb-1.5">
            <label htmlFor="player2Search" className="text-gray-700 text-sm font-bold">
              Player 2 (Red Side 🔴) *
            </label>
            <button
              type="button"
              onClick={handleAddOpponent}
              className="text-xs text-green-700 hover:text-green-800 font-bold transition flex items-center gap-1"
            >
              <span>➕</span> Add Opponent
            </button>
          </div>
          
          <div className="relative">
            <input
              type="text"
              id="player2Search"
              placeholder="Type name to search Player 2... ▾"
              value={searchTermP2}
              onFocus={() => setShowP2Dropdown(true)}
              onChange={(e) => {
                setSearchTermP2(e.target.value);
                setShowP2Dropdown(true);
              }}
              className="shadow appearance-none border border-gray-300 rounded w-full py-2.5 px-3 text-gray-700 leading-tight focus:outline-none focus:border-green-500 text-sm pr-8 bg-white"
              required
            />
            {player2Id && (
              <button
                type="button"
                onClick={() => {
                  setPlayer2Id('');
                  setPlayer2Name('');
                  setSearchTermP2('');
                  setShowP2Dropdown(true);
                }}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 font-bold text-xs"
              >
                ✕
              </button>
            )}
          </div>

          {showP2Dropdown && (
            <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto p-1">
              {filteredOpponentsP2.length === 0 ? (
                <div className="p-3 text-xs text-gray-500 text-center">
                  {searchTermP2 ? 'No matching players found.' : 'No players available.'}
                </div>
              ) : (
                filteredOpponentsP2.map((op) => {
                  const opName = `${op.firstName || ''} ${op.lastName || ''}`.trim() || op.name || 'Opponent';
                  const isSelected = String(player2Id) === String(op.id);
                  return (
                    <button
                      key={op.id}
                      type="button"
                      onClick={() => handleSelectPlayer2(op.id, opName)}
                      className={`w-full text-left px-3 py-2 rounded text-xs transition flex justify-between items-center ${
                        isSelected ? 'bg-red-50 text-red-700 font-bold' : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <span>
                        {opName} {op.utr ? `(UTR: ${op.utr})` : ''}
                      </span>
                      {isSelected && <span className="text-red-600 font-bold">✓ Selected</span>}
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* 3. Match Surface Selector (Single Line Row) */}
        <div>
          <label htmlFor="matchSurface" className="block text-gray-700 text-sm font-bold mb-1.5">
            Match Surface
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { val: 'Hard court', label: '🟦 Hard court' },
              { val: 'Red clay', label: '🧱 Red clay' },
              { val: 'Green Clay', label: '🌲 Green Clay' },
              { val: 'Grass', label: '🌿 Grass' },
            ].map(opt => (
              <button
                key={opt.val}
                type="button"
                onClick={() => setMatchSurface(opt.val)}
                className={`py-2 px-3 rounded-lg text-xs sm:text-sm font-semibold border transition text-center ${
                  matchSurface === opt.val
                    ? 'bg-green-600 border-green-600 text-white shadow-sm'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* 4. Separate Location Field (Single Line Row) */}
        <div>
          <div className="flex justify-between items-center mb-1.5">
            <label htmlFor="matchLocation" className="text-gray-700 text-sm font-bold">
              Location
            </label>
            <button
              type="button"
              onClick={handleAutoDetectLocation}
              disabled={detectingLocation}
              className="text-xs font-semibold text-green-700 hover:text-green-800 bg-green-50 hover:bg-green-100 px-2.5 py-1 rounded transition flex items-center gap-1 border border-green-200"
            >
              {detectingLocation ? '📍 Locating...' : '📍 Auto-fill Location'}
            </button>
          </div>
          <input
            type="text"
            id="matchLocation"
            className="shadow appearance-none border border-gray-300 rounded w-full py-2.5 px-3 text-gray-700 leading-tight focus:outline-none focus:border-green-500 text-sm"
            value={matchLocation}
            onChange={(e) => setMatchLocation(e.target.value)}
            placeholder="e.g. Court 3, Central Park"
          />
        </div>

        <hr className="my-5 border-gray-200" />

        {/* 5. Match Format & Scoring Rules Card */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
          <h3 className="text-md font-bold text-green-700 flex items-center gap-1">
            <span>⚙️</span> Match Format & Scoring Rules
          </h3>

          {/* Number of Sets */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Number of Sets:</label>
            <div className="flex flex-wrap gap-2">
              {[
                { val: 1, label: '1 Set' },
                { val: 3, label: '3 Sets (Best of 3)' },
                { val: 5, label: '5 Sets (Best of 5)' },
              ].map(opt => (
                <button
                  key={opt.val}
                  type="button"
                  onClick={() => setNumberOfSets(opt.val)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                    numberOfSets === opt.val
                      ? 'bg-green-600 border-green-600 text-white'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Games Per Set */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Games per Set:</label>
            <div className="flex flex-wrap gap-2">
              {[
                { val: 4, label: '4 Games (Short)' },
                { val: 6, label: '6 Games (Standard)' },
                { val: 8, label: '8 Games (Pro Set)' },
              ].map(opt => (
                <button
                  key={opt.val}
                  type="button"
                  onClick={() => setGamesPerSet(opt.val)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                    gamesPerSet === opt.val
                      ? 'bg-green-600 border-green-600 text-white'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Deuce / Advantage Rule */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Deuce / Advantage Rule:</label>
            <div className="flex flex-wrap gap-2">
              {[
                { val: 'ad', label: 'Advantage (Standard Deuce)' },
                { val: 'no_ad', label: 'No-Ad (Sudden Death at Deuce)' },
              ].map(opt => (
                <button
                  key={opt.val}
                  type="button"
                  onClick={() => setAdScoring(opt.val)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                    adScoring === opt.val
                      ? 'bg-green-600 border-green-600 text-white'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tie-Break Trigger */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Tie-Break Played At:</label>
            <div className="flex flex-wrap gap-2">
              {[
                { val: 'at_games', label: `${gamesPerSet}:${gamesPerSet} (e.g. 6-6 -> 7-6)` },
                { val: 'at_minus_one', label: `${gamesPerSet - 1}:${gamesPerSet - 1} (e.g. 5-5 -> 6-5)` },
              ].map(opt => (
                <button
                  key={opt.val}
                  type="button"
                  onClick={() => setTieBreakAt(opt.val)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                    tieBreakAt === opt.val
                      ? 'bg-green-600 border-green-600 text-white'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tie-Break Target Points */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Tie-Break Target Points:</label>
            <div className="flex flex-wrap gap-2">
              {[
                { val: 5, label: 'First to 5 (Win by 2)' },
                { val: 7, label: 'First to 7 (Standard)' },
                { val: 10, label: 'First to 10' },
              ].map(opt => (
                <button
                  key={opt.val}
                  type="button"
                  onClick={() => setTieBreakTarget(opt.val)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                    tieBreakTarget === opt.val
                      ? 'bg-green-600 border-green-600 text-white'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Deciding Final Set Type (only if > 1 set) */}
          {numberOfSets > 1 && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Deciding Final Set Type:</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { val: 'super_tiebreak', label: '10-Point Match Tie-Break (Super TB)' },
                  { val: 'regular', label: 'Full Final Set' },
                ].map(opt => (
                  <button
                    key={opt.val}
                    type="button"
                    onClick={() => setFinalSetType(opt.val)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                      finalSetType === opt.val
                        ? 'bg-green-600 border-green-600 text-white'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Super Tie-Break Target Points */}
          {numberOfSets > 1 && finalSetType === 'super_tiebreak' && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Match Tie-Break Target:</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { val: 7, label: 'First to 7 (Win by 2)' },
                  { val: 10, label: 'First to 10 (Standard Super TB)' },
                ].map(opt => (
                  <button
                    key={opt.val}
                    type="button"
                    onClick={() => setSuperTieBreakTarget(opt.val)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                      superTieBreakTarget === opt.val
                        ? 'bg-green-600 border-green-600 text-white'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Initial Server Selection */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Who Serves First:</label>
            <div className="flex flex-wrap gap-2">
              {[
                { val: 'ask', label: '🎾 Ask on Match Start' },
                { val: 'me', label: `🟢 ${player1Name || 'Player 1'}` },
                { val: 'opponent', label: `🔴 ${player2Name || 'Player 2'}` },
              ].map(opt => (
                <button
                  key={opt.val}
                  type="button"
                  onClick={() => setFirstServer(opt.val)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                    firstServer === opt.val
                      ? 'bg-green-600 border-green-600 text-white'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <button
          type="submit"
          className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline transition duration-200 text-lg shadow-md"
        >
          Start Match
        </button>
      </form>

      {/* Opponent Details Overlay */}
      {showOpponentOverlay && (
        <OpponentDetailsOverlay
          opponent={{ firstName: '', lastName: '', utr: '', wtn: '', club: '' }}
          onSave={handleOpponentSave}
          onCancel={handleOpponentCancel}
        />
      )}
    </div>
  );
};

export default NewMatchPage;
